<script setup lang="ts">
import { defineRule, Field as VeeField } from 'vee-validate'

import { requiredPaymentMethod } from '@/utils/rulesOfValidate'

import { useAppStore } from '@/store/app/app'
import { SnackbarType } from '@/store/app/app.types'
import { useBasketStore } from '@/store/basket/basket'
import { useBasketChangeStore } from '@/store/basket/basket-change'
import { useBasketPaymentMethodsStore } from '@/store/basket/basket-payment-methods'
import { useToggleStore } from '@/store/toggle/toggle'

import { Success } from '@/api/results'
import { OfflinePayMethods } from '@/types/api/api-v3/orders/order'
import {
  BasketPayment,
  PaymentMethodType,
} from '@/types/api/api-v3/paymentMethods'
import {
  CheckoutButtonState,
  CheckoutButtonStateInjectType,
} from '@/types/basket'

import BasketCallSelect from '@/components/Basket/basket-payment/BasketCallSelect.vue'
import BasketPaymentInfo from '@/components/Basket/basket-payment/BasketPaymentInfo.vue'
import BasketPaymentList from '@/components/Basket/basket-payment/BasketPaymentList.vue'
import BasketSection from '@/components/Basket/basket-utils/BasketSection.vue'
import BasketTitle from '@/components/Basket/basket-utils/BasketTitle.vue'

const BasketFailed = defineAsyncComponent(
  () => import('@/components/Basket/basket-utils/BasketFailed.vue'),
)

const checkoutButtonState = inject<CheckoutButtonStateInjectType>(
  'checkoutButtonState',
)

const isBasketPaymentLoading = computed(
  () => basketPaymentMethodsStore.isLoading,
)

const { showSnackbar } = useAppStore()
const { $metrics } = useNuxtApp()

const basketStore = useBasketStore()
const basketPaymentMethodsStore = useBasketPaymentMethodsStore()
const basketChangeStore = useBasketChangeStore()
const toggleStore = useToggleStore()

const basket = computed(() => basketStore.data)
const selectedPaymentMethod = computed(() => basket.value.selectedPaymentMethod)
const amountToPay = computed(() => basket.value.amountToPay)
const change = computed(() => basket.value.change)
const methodDelivery = computed(() => basket.value.delivery.method)

const basketPaymentMethods = computed(() => basketPaymentMethodsStore.data)

const updateSelectedPayment = (paymentMethod: BasketPayment | null) => {
  basketStore.setSelectedPaymentMethod(paymentMethod)

  if (paymentMethod?.type === PaymentMethodType.PAY_SELECTION) {
    $metrics?.eventSendGoal({ ym: 'payment_payselection' })
  }
}

// Получаем список оплат
const getBasketPaymentMethods = async () =>
  await basketPaymentMethodsStore.getPaymentMethods()

const changeErrorShow = ref(true)

// Получаем обновленные данные по корзине при выборе наличных со сдачей
const getChange = async () => {
  const result = await basketChangeStore.fetchChange(amountToPay.value)

  if (result instanceof Success) {
    checkChange()
    changeErrorShow.value = false

    return
  }

  changeErrorShow.value = true
}

onMounted(async () => {
  await Promise.all([getBasketPaymentMethods(), getChange()])

  // Устанавливаем первый из списка оплат
  if (basketPaymentMethods.value.length)
    updateSelectedPayment(basketPaymentMethods.value[0])
})

const isOfflinePayment = computed(() => {
  const offlinePayMethods: string[] = Object.values(OfflinePayMethods)
  return offlinePayMethods.includes(selectedPaymentMethod.value?.type || '')
})

const isShowCallBlock = computed(
  () =>
    isOfflinePayment.value &&
    toggleStore.featureToggle.basket_order_checkout_do_not_call_me_back_enabled,
)

const isChangeExist = ref(true)

const checkChange = () => {
  // проверка на наличие текущей купюры для  сдачи в новом списке купюр
  const changeList: number[] = []

  basketChangeStore.choices.map((item) => ({
    changeList: changeList.push(item.value),
  }))

  isChangeExist.value = changeList.includes(change.value)
}

// Уведомляем, если сумма для оплаты > выбранной купюры для сдачи
watch(isChangeExist, (value) => {
  if (value) {
    checkoutButtonState?.set(null)
    return
  }

  checkoutButtonState?.set(CheckoutButtonState.changeExchange)

  showSnackbar({
    message: 'Измените сдачу',
    type: SnackbarType.MESSAGE,
  })
})

watch(change, checkChange)

watch(amountToPay, getChange)

/*
  Наблюдатель за отслеживанием текущих доступных способов оплаты в зависимости от:
    * Адреса доставки/метода доставки
    * Времени и "Умного распределения"
*/
watch(
  () => basketPaymentMethodsStore.isLoading,
  (currentValue, prevValue) => {
    // Если нет текущего выбранного способа опаты - выходим
    if (!basket.value.selectedPaymentMethod) return

    // Если флаг isLoading из false становится true - выходим
    if (currentValue || !prevValue) return

    // Текущие способы оплаты
    const paymentMethods = basketPaymentMethodsStore.data
    // Текущий выбранный тип способа оплаты
    const selectedPaymentMethodType = basket.value.selectedPaymentMethod?.type

    // Мапимся и убеждаемся, что после перезагрузки у нас остался выбранный способ оплаты
    const isSelectedPaymentMethodAvailable = paymentMethods.some(
      ({ type }) => type === selectedPaymentMethodType,
    )

    // Если выбранный способ олаты недоступен, чистим поле выбранной оплаты в корзине
    if (!isSelectedPaymentMethodAvailable)
      basketStore.setSelectedPaymentMethod(null)
  },
)

defineRule('required-payment-method', requiredPaymentMethod)
</script>

<template>
  <basket-section
    id="payment_method"
    :loading="isBasketPaymentLoading"
    class="basket-payment"
  >
    <basket-title class="basket-payment__title" title="Способ оплаты" />

    <basket-failed
      v-if="basketPaymentMethodsStore.error"
      error-message="Не удалось загрузить способы оплаты"
      @update="getBasketPaymentMethods"
    />

    <vee-field
      v-else
      v-slot="{ errors, field }"
      name="basket-payment"
      vid="basket-payment"
      rules="required-payment-method"
      :model-value="selectedPaymentMethod"
    >
      <div class="payment-methods">
        <basket-payment-list
          v-bind="field"
          :basket-payment-methods="basketPaymentMethods"
          :method-delivery="methodDelivery"
          :selected-payment-method="selectedPaymentMethod"
          @update:selected-payment="updateSelectedPayment"
        />

        <basket-payment-info
          :selected-payment-method="selectedPaymentMethod"
          :method-delivery="methodDelivery"
          :change-error-show="changeErrorShow"
          :validate-errors="errors"
          :is-change-exist="isChangeExist"
          :change="change"
          :amount-to-pay="amountToPay"
          @update:change="getChange"
        />
      </div>

      <template v-if="isShowCallBlock">
        <transition-group name="height">
          <hr class="hr" />

          <basket-call-select id="call" />
        </transition-group>
      </template>
    </vee-field>
  </basket-section>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/animations.scss' as *;

.basket-payment {
  overflow: hidden;

  &__title {
    margin-bottom: 0;
  }

  .hr {
    margin: 25px 0 20px;
    border: 1px solid var(--ui-grey);

    @include media('<=portrait-phone') {
      margin-top: 20px;
    }
  }

  .payment-methods {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
  }
}
</style>
